import { Component, Inject, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, first, map, switchMap } from 'rxjs/operators';
import { BackendApiService } from 'src/app/services/backend-api.service';
import { resolveModuleResourcePath } from 'src/app/constants/module-ids.constant';
import { ToasterService } from 'src/app/services/core/toaster.service';
import { TaskDataService } from '../task.data.service';

export interface CopyTaskDialogData {
  taskId?: number | string;
  taskTitle?: string;
  moduleId?: number | string;
  referenceId?: number | string;
  referenceName?: string;
}

@Component({
    selector: 'app-copy-task-dialog',
    templateUrl: './copy-task-dialog.component.html',
    styleUrls: ['./copy-task-dialog.component.scss'],
    standalone: false
})
export class CopyTaskDialogComponent implements OnInit {
  public sectionForm!: UntypedFormGroup;
  public modules: any[] = [];
  public isLoading = false;
  public isModulesLoading = false;
  public referenceSearchInput$ = new Subject<string>();
  public referenceSearchResults$: Observable<any[]> = of([]);
  public referenceSearchOptions: any[] = [];
  public referenceSearchLoading = false;

  constructor(
    public dialogRef: MatDialogRef<CopyTaskDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CopyTaskDialogData,
    private backendApiService: BackendApiService,
    private toasterService: ToasterService,
    private taskDataService: TaskDataService
  ) {}

  ngOnInit(): void {
    this.sectionForm = new UntypedFormGroup({
      title: new UntypedFormControl(
        this.data?.taskTitle ? `${this.data.taskTitle} Copy` : '',
        [Validators.required, Validators.maxLength(255)]
      ),
      module: new UntypedFormControl(null, [Validators.required]),
      reference_id: new UntypedFormControl(this.data?.referenceId ?? null, [Validators.required]),
    });

    this.setupReferenceSearch();
    this.referenceSearchResults$.subscribe((results: any[]) => {
      this.referenceSearchOptions = results || [];
    });

    // Ensure reference field is visibly prefilled immediately on popup open with name.
    if (this.data?.referenceId && this.data?.referenceName) {
      const prefillId = Number(this.data.referenceId);
      this.referenceSearchOptions = [{ id: prefillId, name: this.data.referenceName }];
      this.sectionForm.get('reference_id')?.setValue(prefillId);
    }

    this.loadModules();
  }

  private loadModules(): void {
    this.isModulesLoading = true;
    this.backendApiService
      .getApi('modules/type?type=task')
      .pipe(first())
      .subscribe({
        next: (response: any) => {
          this.modules = response?.data || [];
          if (this.data?.moduleId) {
            const currentModule = this.modules.find(
              (item: any) => String(item?.id) === String(this.data?.moduleId)
            );
            if (currentModule) {
              this.sectionForm.get('module')?.setValue(currentModule);
              if (this.data?.referenceId) {
                this.setInitialReferenceSelection(this.data.referenceId);
              } else {
                this.autoFillFirstReferenceForModule(currentModule);
              }
            }
          }
          this.isModulesLoading = false;
        },
        error: () => {
          this.modules = [];
          this.isModulesLoading = false;
        },
      });
  }

  onModuleChange(): void {
    this.sectionForm.get('reference_id')?.setValue(null);
    const module = this.sectionForm?.get('module')?.value;
    if (!module?.id || !module?.slug) {
      this.referenceSearchOptions = [];
      this.referenceSearchResults$ = of([]);
      return;
    }
    this.autoFillFirstReferenceForModule(module);
  }

  private setupReferenceSearch(): void {
    this.referenceSearchResults$ = this.referenceSearchInput$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((searchTerm: string) => {
        const module = this.sectionForm?.get('module')?.value;
        if (!module?.id || !module?.slug || !searchTerm || searchTerm.trim().length < 1) {
          this.referenceSearchLoading = false;
          return of([]);
        }
        return this.fetchReferenceOptions(module, searchTerm.trim());
      })
    );
  }

  private fetchReferenceOptions(module: any, searchTerm: string): Observable<any[]> {
    const payload = {
      type: module.slug,
      module_id: module.id,
      search: searchTerm,
    };

    this.referenceSearchLoading = true;
    return this.backendApiService.postApi('tasks/search-reference-numbers', payload).pipe(
      map((response: any) => {
        this.referenceSearchLoading = false;
        const list = response?.data?.data || [];
        return list.map((x: any) => ({
          id: x.id,
          name: x.data,
        }));
      }),
      catchError(() => {
        this.referenceSearchLoading = false;
        return of([]);
      })
    );
  }

  private autoFillFirstReferenceForModule(module: any): void {
    this.fetchReferenceOptions(module, '').pipe(first()).subscribe((results: any[]) => {
      this.referenceSearchOptions = results || [];
      this.referenceSearchResults$ = of(results || []);
      if (results?.length) {
        this.sectionForm.get('reference_id')?.setValue(results[0].id);
      }
    });
  }

  private setInitialReferenceSelection(referenceId: number | string): void {
    const module = this.sectionForm?.get('module')?.value;
    const resourcePath = resolveModuleResourcePath(module);
    if (!resourcePath || !referenceId) {
      return;
    }
    this.backendApiService.getApi(`${resourcePath}/${referenceId}`).pipe(first()).subscribe({
      next: (response: any) => {
        const ref = response?.data;
        if (!ref?.id) {
          return;
        }
        const name =
          ref.reference_number ||
          ref.ref_number ||
          ref.bid_number ||
          ref.job_number ||
          ref.proposal_number ||
          ref.name ||
          ref.title ||
          String(ref.id);
        const initialOption = { id: ref.id, name };
        this.referenceSearchOptions = [initialOption];
        this.referenceSearchResults$ = of([initialOption]);
        this.sectionForm.get('reference_id')?.setValue(ref.id);
      },
      error: () => {},
    });
  }

  onCopy(): void {
    if (this.sectionForm.invalid || !this.data?.taskId) {
      this.touchFormControls();
      return;
    }

    const selectedModule = this.sectionForm.value.module;
    const title = (this.sectionForm.value.title || '').trim();
    const referenceId = Number(this.sectionForm.value.reference_id);
    const payload = {
      title,
      module_id: selectedModule?.id ?? selectedModule,
      reference_id: referenceId,
    };

    this.isLoading = true;
    this.taskDataService
      .copy(this.data.taskId, payload)
      .pipe(first())
      .subscribe({
        next: (response: any) => {
          this.isLoading = false;
          this.toasterService.showSuccessMessage(response?.data?.message);
          const newId =
            response?.data?.data?.id ??
            response?.data?.task?.id ??
            response?.data?.id;
          this.close({ copied: true, newId });
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  touchFormControls(): void {
    Object.keys(this.sectionForm.controls).forEach((key) => {
      this.sectionForm.get(key)?.markAsTouched();
    });
  }

  close(data: any = null): void {
    this.dialogRef.close(data);
  }
}
